/* ==========================================================================
   Ignite Theme — JavaScript
   ========================================================================== */

'use strict';

/* --------------------------------------------------------------------------
   1. Header: Transparent → Solid on scroll
   -------------------------------------------------------------------------- */
(function initHeader() {
  const header = document.getElementById('site-header');
  if (!header) return;

  const threshold = 60;

  const updateHeader = () => {
    if (window.scrollY > threshold) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  };

  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });
})();

/* --------------------------------------------------------------------------
   2. Mobile Menu Toggle
   -------------------------------------------------------------------------- */
(function initMobileMenu() {
  const toggleBtn = document.getElementById('mobile-menu-toggle');
  const menu      = document.getElementById('mobile-menu');
  const hamburger = document.getElementById('hamburger-icon');
  const closeBtn  = document.getElementById('mobile-menu-close');
  const body      = document.body;

  if (!toggleBtn || !menu) return;

  const openMenu = () => {
    menu.classList.add('open');
    hamburger && hamburger.classList.add('open');
    body.style.overflow = 'hidden';
    menu.setAttribute('aria-hidden', 'false');
    toggleBtn.setAttribute('aria-expanded', 'true');
  };

  const closeMenu = () => {
    menu.classList.remove('open');
    hamburger && hamburger.classList.remove('open');
    body.style.overflow = '';
    menu.setAttribute('aria-hidden', 'true');
    toggleBtn.setAttribute('aria-expanded', 'false');
  };

  toggleBtn.addEventListener('click', () => {
    menu.classList.contains('open') ? closeMenu() : openMenu();
  });

  closeBtn && closeBtn.addEventListener('click', closeMenu);

  // Close on overlay click
  menu.addEventListener('click', (e) => {
    if (e.target === menu) closeMenu();
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.classList.contains('open')) closeMenu();
  });

  // Close when a nav link is clicked
  menu.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', closeMenu);
  });
})();

/* --------------------------------------------------------------------------
   3. Scroll-triggered Animations (IntersectionObserver)
   -------------------------------------------------------------------------- */
(function initScrollAnimations() {
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('[data-animate]').forEach((el) => {
      el.classList.add('animated');
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animated');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
  );

  document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
})();

/* --------------------------------------------------------------------------
   4. Counter Animation (Stats)
   -------------------------------------------------------------------------- */
(function initCounters() {
  const counters = document.querySelectorAll('[data-counter]');
  if (!counters.length) return;

  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  const animateCounter = (el) => {
    const target   = parseFloat(el.dataset.counter);
    const duration = parseInt(el.dataset.duration || 2000, 10);
    const suffix   = el.dataset.suffix || '';
    const prefix   = el.dataset.prefix || '';
    const decimals = (target % 1 !== 0) ? String(target).split('.')[1].length : 0;
    const start    = performance.now();

    const tick = (now) => {
      const elapsed  = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const value    = easeOutQuart(progress) * target;
      el.textContent = prefix + value.toFixed(decimals) + suffix;
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  };

  if (!('IntersectionObserver' in window)) {
    counters.forEach(animateCounter);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.5 }
  );

  counters.forEach((el) => observer.observe(el));
})();

/* --------------------------------------------------------------------------
   5. Case Studies Slider
   -------------------------------------------------------------------------- */
(function initSliders() {
  document.querySelectorAll('[data-slider]').forEach((slider) => {
    const track    = slider.querySelector('[data-slider-track]');
    const slides   = slider.querySelectorAll('[data-slide]');
    const prevBtn  = slider.querySelector('[data-slider-prev]');
    const nextBtn  = slider.querySelector('[data-slider-next]');
    const dotsWrap = slider.querySelector('[data-slider-dots]');

    if (!track || !slides.length) return;

    let current    = 0;
    let isDragging = false;
    let startX     = 0;
    let scrollLeft = 0;

    // Compute visible slides count (responsive)
    const getVisibleCount = () => {
      const sliderW = slider.offsetWidth;
      const slideW  = slides[0] ? slides[0].offsetWidth : sliderW;
      return Math.round(sliderW / (slideW + 24)); // 24 = gap
    };

    // Build dots
    if (dotsWrap) {
      slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.className = 'slider-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => goTo(i));
        dotsWrap.appendChild(dot);
      });
    }

    const updateDots = () => {
      if (!dotsWrap) return;
      dotsWrap.querySelectorAll('.slider-dot').forEach((dot, i) => {
        dot.classList.toggle('active', i === current);
      });
    };

    const updateButtons = () => {
      const maxIndex = slides.length - getVisibleCount();
      if (prevBtn) prevBtn.disabled = current <= 0;
      if (nextBtn) nextBtn.disabled = current >= maxIndex;
    };

    const goTo = (index) => {
      const maxIndex = Math.max(0, slides.length - getVisibleCount());
      current = Math.max(0, Math.min(index, maxIndex));
      const slideW = slides[0] ? slides[0].offsetWidth + 24 : 0;
      track.style.transform = `translateX(${-current * slideW}px)`;
      updateDots();
      updateButtons();
    };

    prevBtn && prevBtn.addEventListener('click', () => goTo(current - 1));
    nextBtn && nextBtn.addEventListener('click', () => goTo(current + 1));

    // Drag / touch support
    const onDragStart = (x) => { isDragging = true; startX = x; scrollLeft = current; };
    const onDragEnd   = (x) => {
      if (!isDragging) return;
      isDragging = false;
      const diff = startX - x;
      if (Math.abs(diff) > 60) goTo(diff > 0 ? current + 1 : current - 1);
    };

    track.addEventListener('mousedown',  (e) => onDragStart(e.clientX));
    track.addEventListener('mouseup',    (e) => onDragEnd(e.clientX));
    track.addEventListener('mouseleave', (e) => { if (isDragging) onDragEnd(e.clientX); });
    track.addEventListener('touchstart', (e) => onDragStart(e.touches[0].clientX), { passive: true });
    track.addEventListener('touchend',   (e) => onDragEnd(e.changedTouches[0].clientX));

    // Keyboard
    slider.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  goTo(current - 1);
      if (e.key === 'ArrowRight') goTo(current + 1);
    });

    // Auto-play (if data-autoplay set)
    const autoplayInterval = parseInt(slider.dataset.autoplay, 10);
    if (autoplayInterval > 0) {
      let autoplay = setInterval(() => {
        const maxIndex = slides.length - getVisibleCount();
        goTo(current >= maxIndex ? 0 : current + 1);
      }, autoplayInterval);

      slider.addEventListener('mouseenter', () => clearInterval(autoplay));
      slider.addEventListener('mouseleave', () => {
        autoplay = setInterval(() => {
          const maxIndex = slides.length - getVisibleCount();
          goTo(current >= maxIndex ? 0 : current + 1);
        }, autoplayInterval);
      });
    }

    updateButtons();
    window.addEventListener('resize', () => { goTo(0); });
  });
})();

/* --------------------------------------------------------------------------
   6. Contact Form: Client-side Validation + Submission
   -------------------------------------------------------------------------- */
(function initContactForm() {
  const form = document.getElementById('contact-form-pro');
  if (!form) return;

  const showError = (input, msg) => {
    input.classList.add('error');
    let errEl = input.parentElement.querySelector('.form-error-msg');
    if (!errEl) {
      errEl = document.createElement('span');
      errEl.className = 'form-error-msg';
      input.parentElement.appendChild(errEl);
    }
    errEl.textContent = msg;
  };

  const clearError = (input) => {
    input.classList.remove('error');
    const errEl = input.parentElement.querySelector('.form-error-msg');
    if (errEl) errEl.remove();
  };

  const validateField = (input) => {
    const val   = input.value.trim();
    const type  = input.type;
    const name  = input.name;

    if (input.required && !val) {
      showError(input, 'This field is required.');
      return false;
    }
    if (type === 'email' && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      showError(input, 'Please enter a valid email address.');
      return false;
    }
    if ((name === 'contact[phone]' || name === 'phone') && val && !/^[\d\s\-\+\(\)]{7,15}$/.test(val)) {
      showError(input, 'Please enter a valid phone number.');
      return false;
    }
    clearError(input);
    return true;
  };

  form.querySelectorAll('[required], input[type="email"]').forEach((input) => {
    input.addEventListener('blur', () => validateField(input));
    input.addEventListener('input', () => clearError(input));
  });

  form.addEventListener('submit', (e) => {
    const fields = form.querySelectorAll('input, textarea, select');
    let isValid  = true;

    fields.forEach((field) => {
      if (!validateField(field)) isValid = false;
    });

    if (!isValid) {
      e.preventDefault();
      return;
    }

    // Valid: let the browser submit natively so Shopify can run its
    // spam challenge (captcha) and report the real result on reload.
    const submitBtn  = form.querySelector('[type="submit"]');
    const btnTexts   = submitBtn.querySelectorAll('.btn-text');
    const btnLoader  = submitBtn.querySelector('.btn-loader');

    btnTexts.forEach((el) => (el.style.display = 'none'));
    btnLoader.style.display = 'flex';
    submitBtn.disabled = true;
  });
})();

/* --------------------------------------------------------------------------
   7. Toast Notifications
   -------------------------------------------------------------------------- */
function showToast(message, type = 'success', duration = 4000) {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = `toast ${type}`;

  const icon = type === 'success' ? '✓' : '✕';
  toast.innerHTML = `
    <span style="
      width:24px;height:24px;border-radius:50%;background:${type === 'success' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'};
      display:flex;align-items:center;justify-content:center;
      color:${type === 'success' ? '#22c55e' : '#ef4444'};font-weight:700;flex-shrink:0;
    ">${icon}</span>
    <span>${message}</span>
  `;

  document.body.appendChild(toast);
  requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('show')));

  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 400);
  }, duration);
}

/* --------------------------------------------------------------------------
   8. Smooth Anchor Scroll
   -------------------------------------------------------------------------- */
(function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (e) => {
      const href   = link.getAttribute('href');
      if (href === '#') return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      const offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '80', 10);
      const top    = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  });
})();

/* --------------------------------------------------------------------------
   9. Cart: Quantity Buttons
   -------------------------------------------------------------------------- */
(function initCartQuantity() {
  document.querySelectorAll('.quantity-selector').forEach((selector) => {
    const input   = selector.querySelector('.quantity-input');
    const minusBtn = selector.querySelector('[data-qty-minus]');
    const plusBtn  = selector.querySelector('[data-qty-plus]');
    if (!input) return;

    const update = (delta) => {
      const min  = parseInt(input.min || 1, 10);
      const max  = parseInt(input.max || 99, 10);
      const next = Math.min(max, Math.max(min, parseInt(input.value, 10) + delta));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    minusBtn && minusBtn.addEventListener('click', () => update(-1));
    plusBtn  && plusBtn.addEventListener('click',  () => update(1));
  });
})();

/* --------------------------------------------------------------------------
   10. Active Nav Link Highlight (based on current path)
   -------------------------------------------------------------------------- */
(function initActiveNavLinks() {
  const path = window.location.pathname;
  document.querySelectorAll('.nav-link').forEach((link) => {
    const href = link.getAttribute('href');
    if (href && href !== '/' && path.startsWith(href)) {
      link.classList.add('active');
    } else if (href === '/' && path === '/') {
      link.classList.add('active');
    }
  });
})();

/* --------------------------------------------------------------------------
   11. Mega Menu: Keyboard Navigation
   -------------------------------------------------------------------------- */
(function initMegaMenu() {
  document.querySelectorAll('.mega-menu-trigger').forEach((trigger) => {
    const menu = trigger.querySelector('.mega-menu');
    if (!menu) return;

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') menu.blur();
    });
  });
})();

/* --------------------------------------------------------------------------
   12. Video Section: Autoplay on intersection
   -------------------------------------------------------------------------- */
(function initVideoAutoplay() {
  if (!('IntersectionObserver' in window)) return;

  document.querySelectorAll('video[data-autoplay]').forEach((video) => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.3 }
    );
    observer.observe(video);
  });
})();
